import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Fund from '@/models/Fund';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import { parseSupportRequestsExcel, type ParseResult } from './shared/excelParser';
import { loadBankDirectory } from './shared/bankResolution';
import type {
  BulkSupportRequestRow,
  BulkCreateResult,
  BulkCreateSupportRequestsPayload,
  UploadStep,
} from './shared/types';
import FileUploadArea from './shared/FileUploadArea';
import ConfirmDialog from './shared/ConfirmDialog';
import ResultsTable from './shared/ResultsTable';
import styles from './shared/SharedStyles.module.css';

export default function UploadSupportRequestsTab({ targetFund }: { targetFund: Fund | null }) {
  const { t } = useTranslation();

  const [step, setStep] = useState<UploadStep>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult<BulkSupportRequestRow> | null>(null);
  const [result, setResult] = useState<BulkCreateResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const { mutateAsync: bulkCreate, isPending } = usePostAuthApi<
    BulkCreateResult,
    BulkCreateSupportRequestsPayload
  >('management/bulk-uploads/support-requests');

  const handleFileSelected = async (selectedFile: File) => {
    setFile(selectedFile);
    setParseError(null);
    try {
      const parsed = await parseSupportRequestsExcel(selectedFile, await loadBankDirectory());
      if (parsed.rows.length === 0 && parsed.errors.length === 0) {
        setParseError(t('bulkUploads.noDataInFile'));
        return;
      }
      setParseResult(parsed);
      setStep('confirm');
    } catch (err) {
      setParseError(err instanceof Error ? err.message : t('bulkUploads.parseError'));
    }
  };

  const handleConfirm = async () => {
    if (!parseResult || !targetFund) return;

    try {
      const response = await bulkCreate({
        fundId: targetFund.id,
        rows: parseResult.rows,
      });
      setResult(response);
      setStep('results');
    } catch {
      setParseError(t('bulkUploads.serverError'));
    }
  };

  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setParseResult(null);
    setResult(null);
    setParseError(null);
  };

  return (
    <div>
      {file && step !== 'upload' && (
        <div className={styles.fileInfo}>
          <span className={`${styles.fileInfoName} body-md-semibold`}>{file.name}</span>
          <Button variant="outline" onClick={handleReset} disabled={isPending}>
            {t('bulkUploads.chooseAnother')}
          </Button>
        </div>
      )}

      {step === 'upload' && !targetFund && (
        <p className={`body-md-regular ${styles.selectFundHint}`}>{t('bulkUploads.selectFundFirst')}</p>
      )}

      {step === 'upload' && targetFund && (
        <>
          <FileUploadArea
            onFileSelected={handleFileSelected}
            templateApiUrl="management/bulk-uploads/templates/support-requests"
            templateFileName="bulk-support-requests-template.xlsx"
          />
          {parseError && <p className={`body-md-regular ${styles.errorText}`}>{parseError}</p>}
        </>
      )}

      {step === 'confirm' && parseResult && targetFund && (
        <ConfirmDialog
          fundName={targetFund.name}
          fundId={targetFund.id}
          rowCount={parseResult.rows.length}
          parseErrors={parseResult.errors}
          onConfirm={handleConfirm}
          onCancel={handleReset}
          isLoading={isPending}
          featureLabel={t('bulkUploads.createSupportRequests')}
        />
      )}

      {step === 'results' && result && (
        <>
          <ResultsTable result={result} exportFileName="bulk-support-requests-errors.xlsx" />
          <div className={styles.centerActions}>
            <Button variant="outline" onClick={handleReset}>
              {t('bulkUploads.uploadAnother')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
